import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Download, ExternalLink, ZoomIn, ZoomOut, FileWarning, FileText } from "lucide-react";

export const InvoiceDocumentViewer = ({ invoiceId, document: doc }) => {
  const [url, setUrl] = useState(null);
  const [text, setText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    async function load() {
      if (!doc) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/invoices/${invoiceId}/document`, { responseType: "blob" });
        if (cancelled) return;
        const blob = res.data;
        if ((doc.content_type || "").includes("xml")) {
          setText(await blob.text());
        } else {
          objectUrl = URL.createObjectURL(blob);
          setUrl(objectUrl);
        }
      } catch (e) {
        if (!cancelled) setError("Kunne ikke laste dokumentet.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [invoiceId, doc]);

  const download = () => {
    if (!url && !text) return;
    const a = window.document.createElement("a");
    if (url) {
      a.href = url;
    } else {
      a.href = URL.createObjectURL(new Blob([text], { type: "application/xml" }));
    }
    a.download = doc?.filename || "dokument";
    a.click();
  };

  if (!doc) {
    return (
      <div className="h-full min-h-[320px] rounded-xl border bg-secondary flex flex-col items-center justify-center text-center p-6" data-testid="document-viewer-empty">
        <FileText className="h-10 w-10 text-muted-foreground mb-3" />
        <p className="font-medium">Ingen dokument</p>
        <p className="text-sm text-muted-foreground">Denne fakturaen ble registrert uten vedlegg.</p>
      </div>
    );
  }

  const isPdf = (doc.content_type || "").includes("pdf");
  const isImage = (doc.content_type || "").startsWith("image/");

  return (
    <div className="flex flex-col h-full rounded-xl border bg-secondary overflow-hidden" data-testid="document-viewer">
      <div className="flex items-center justify-between gap-2 border-b bg-card px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm font-medium truncate" title={doc.filename}>{doc.filename}</p>
          <p className="text-xs text-muted-foreground">{isPdf ? "PDF" : isImage ? "Bilde" : "EHF / XML"}{doc.size ? ` · ${(doc.size / 1024).toFixed(0)} KB` : ""}</p>
        </div>
        <div className="flex items-center gap-1">
          {isImage && (
            <>
              <Button variant="ghost" size="icon" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} aria-label="Zoom ut" data-testid="document-zoom-out"><ZoomOut className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setZoom((z) => Math.min(3, z + 0.25))} aria-label="Zoom inn" data-testid="document-zoom-in"><ZoomIn className="h-4 w-4" /></Button>
            </>
          )}
          {url && (
            <Button variant="ghost" size="icon" asChild aria-label="Åpne i ny fane">
              <a href={url} target="_blank" rel="noreferrer" data-testid="document-open-new-tab"><ExternalLink className="h-4 w-4" /></a>
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={download} aria-label="Last ned" data-testid="document-download"><Download className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="flex-1 min-h-[320px] overflow-auto">
        {loading ? (
          <div className="p-4 space-y-3">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center p-6 text-center">
            <FileWarning className="h-8 w-8 text-destructive mb-2" />
            <p className="font-medium">{error}</p>
            <Button variant="outline" className="mt-3" onClick={download}>Last ned dokument</Button>
          </div>
        ) : text !== null ? (
          <div className="min-h-full bg-card">
            <div className="px-4 py-3 border-b bg-accent text-accent-foreground text-sm flex items-start gap-2">
              <FileText className="h-4 w-4 mt-0.5 shrink-0" />
              <p>Dette er en elektronisk faktura (EHF / UBL 2.1). Feltene er lest direkte fra filen uten AI og vises i fakturadetaljene. Under ser du det tekniske innholdet.</p>
            </div>
            <pre className="p-4 text-xs leading-5 whitespace-pre-wrap break-all" data-testid="document-xml">{text}</pre>
          </div>
        ) : isPdf ? (
          <iframe title="Fakturadokument" src={`${url}#toolbar=0&navpanes=0`} className="w-full h-full min-h-[520px] bg-white" data-testid="document-pdf-frame" />
        ) : (
          <div className="p-4 flex justify-center">
            <img src={url} alt="Fakturadokument" style={{ width: `${zoom * 100}%`, maxWidth: "none" }} className="rounded-md shadow-sm bg-white" data-testid="document-image" />
          </div>
        )}
      </div>
    </div>
  );
};
